import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  AddClosingDateRangePayload,
  BranchDetail,
  BranchLogoRemoveParams,
  BranchLogoUploadParams,
  BranchLogoUploadResult,
  CreateTimeWorkingRangePayload,
  TimeWorkingRange,
  UpdateBranchDataPayload,
  UpdateTimeWorkingRangePayload,
  UpdateTimeWorkingRangeStatusPayload,
} from '../types/local-configuration-api.types';

export const localConfigurationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBranchByCode: builder.query<BranchDetail, string>({
      query: (branchCode) => `/branches/${branchCode}`,
      transformResponse: (
        response: Pro20ApiResponse<BranchDetail> | BranchDetail,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),

    /** Public/guest-friendly branch payload (includes closingDateRanges). */
    getBranchDataByBranchCode: builder.query<BranchDetail, string>({
      query: (branchCode) => ({
        url: `/branchdatabybranchcode?branchCode=${encodeURIComponent(branchCode)}`,
        method: 'GET',
      }),
      transformResponse: (
        response: Pro20ApiResponse<BranchDetail> | BranchDetail,
      ) => normalizePro20Response(response),
      providesTags: (_result, _error, branchCode) => [
        { type: API_TAGS.Branch, id: `DATA-${branchCode}` },
      ],
    }),

    updateBranchData: builder.mutation<BranchDetail, UpdateBranchDataPayload>({
      query: (body) => ({
        url: '/updatebranchdata',
        method: 'PUT',
        body,
      }),
      transformResponse: (
        response: Pro20ApiResponse<BranchDetail> | BranchDetail,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, body) => [
        { type: API_TAGS.Branch, id: body.branchCode },
      ],
    }),

    addClosingDateRange: builder.mutation<
      BranchDetail,
      { branchCode: string; body: AddClosingDateRangePayload }
    >({
      query: ({ branchCode, body }) => ({
        url: `/update/restaurantconf/${encodeURIComponent(branchCode)}/addclosingdaterange`,
        method: 'PUT',
        body,
      }),
      transformResponse: (
        response: Pro20ApiResponse<BranchDetail> | BranchDetail,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),

    deleteClosingDateRange: builder.mutation<
      BranchDetail,
      { branchCode: string; dateTimeRangeCode: string }
    >({
      query: ({ branchCode, dateTimeRangeCode }) => ({
        url: `/update/restaurantconf/${encodeURIComponent(branchCode)}/closingDateRange/${encodeURIComponent(dateTimeRangeCode)}`,
        method: 'DELETE',
      }),
      transformResponse: (
        response: Pro20ApiResponse<BranchDetail> | BranchDetail,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),

    uploadBranchLogo: builder.mutation<
      BranchLogoUploadResult,
      BranchLogoUploadParams
    >({
      query: ({ branchCode, formData }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/branch/upload/logo`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (
        response:
          | Pro20ApiResponse<BranchLogoUploadResult>
          | BranchLogoUploadResult,
      ) => normalizePro20Response(response),
      // Avoid Branch invalidation — would reset dirty form fields mid-edit.
      invalidatesTags: [],
    }),

    removeBranchLogo: builder.mutation<null, BranchLogoRemoveParams>({
      query: ({ branchCode }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/branch/remove/logo`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<null> | null) => {
        if (response && typeof response === 'object' && 'code' in response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: [],
    }),

    createTimeWorkingRange: builder.mutation<
      TimeWorkingRange,
      { branchCode: string; body: CreateTimeWorkingRangePayload }
    >({
      query: ({ branchCode, body }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/time-working-ranges`,
        method: 'POST',
        body,
      }),
      transformResponse: (
        response: Pro20ApiResponse<TimeWorkingRange> | TimeWorkingRange,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),

    updateTimeWorkingRange: builder.mutation<
      TimeWorkingRange,
      {
        branchCode: string;
        timeRangeCode: string;
        body: UpdateTimeWorkingRangePayload;
      }
    >({
      query: ({ branchCode, timeRangeCode, body }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/time-working-ranges/${encodeURIComponent(timeRangeCode)}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (
        response: Pro20ApiResponse<TimeWorkingRange> | TimeWorkingRange,
      ) => normalizePro20Response(response),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),

    updateTimeWorkingRangeStatus: builder.mutation<
      TimeWorkingRange | null,
      {
        branchCode: string;
        timeRangeCode: string;
        body: UpdateTimeWorkingRangeStatusPayload;
      }
    >({
      query: ({ branchCode, timeRangeCode, body }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/time-working-ranges/${encodeURIComponent(timeRangeCode)}/status`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => {
        if (response == null) {
          return null;
        }
        if (typeof response === 'object' && 'code' in response) {
          const envelope = response as Pro20ApiResponse<TimeWorkingRange | null>;
          if (envelope.code < 200 || envelope.code >= 300) {
            throw new Error(envelope.message || 'Request failed');
          }
          return envelope.results ?? null;
        }
        if (
          typeof response === 'object' &&
          'success' in response &&
          !(response as { success: boolean }).success
        ) {
          throw new Error(
            (response as { message?: string }).message || 'Request failed',
          );
        }
        if (
          typeof response === 'object' &&
          'success' in response &&
          'results' in response
        ) {
          return (
            (response as { results: TimeWorkingRange | null }).results ?? null
          );
        }
        return response as TimeWorkingRange;
      },
      async onQueryStarted(
        { branchCode, timeRangeCode, body },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          localConfigurationApi.util.updateQueryData(
            'getBranchByCode',
            branchCode,
            (draft) => {
              const applyActive = (range: TimeWorkingRange | null | undefined) => {
                if (range?.timeRangeCode === timeRangeCode) {
                  range.active = body.active;
                }
              };

              if (Array.isArray(draft.timeWorkingRanges)) {
                for (const range of draft.timeWorkingRanges) {
                  applyActive(range);
                }
              }
              applyActive(draft.breakfastTimeWorkingRange);
              applyActive(draft.daylyTimeWorkingRange);
              applyActive(draft.happyhourTimeWorkingRange);
              applyActive(draft.nightTimeWorkingRange);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, error, { branchCode }) =>
        error ? [] : [{ type: API_TAGS.Branch, id: branchCode }],
    }),

    deleteTimeWorkingRange: builder.mutation<
      null,
      { branchCode: string; timeRangeCode: string }
    >({
      query: ({ branchCode, timeRangeCode }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/time-working-ranges/${encodeURIComponent(timeRangeCode)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<null> | null) => {
        if (response && typeof response === 'object' && 'code' in response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Branch, id: branchCode },
      ],
    }),
  }),
});

export const {
  useGetBranchByCodeQuery,
  useLazyGetBranchByCodeQuery,
  useGetBranchDataByBranchCodeQuery,
  useUpdateBranchDataMutation,
  useAddClosingDateRangeMutation,
  useDeleteClosingDateRangeMutation,
  useUploadBranchLogoMutation,
  useRemoveBranchLogoMutation,
  useCreateTimeWorkingRangeMutation,
  useUpdateTimeWorkingRangeMutation,
  useUpdateTimeWorkingRangeStatusMutation,
  useDeleteTimeWorkingRangeMutation,
} = localConfigurationApi;
